import { SvgIcon } from "@progress/kendo-react-common";
import { trendUpIcon } from "@progress/kendo-svg-icons";
import { useMemo } from "react";
import { isCommercialPlanData } from "../../../../models/agentResponseModels";
import type { CommercialPlanData } from "../../../../models/commericalPlanModels";
import type { ScenarioLibraryScenario } from "../../../../models/scenarioLibraryScenario";
import { calculateCommercialPlanMetrics, calculateTotalExpectedVelocity } from "../../../../utils/commercialPlanCalculations";
import { isDefinedNumber } from "../../../../utils/numberValueConversion";

type ScenarioComparisonSummaryProps = {
    scenarios: (ScenarioLibraryScenario | null)[];
};

type ComparedScenario = {
    scenario: ScenarioLibraryScenario;
    slotIndex: number;
    profit: number | null;
    revenue: number | null;
    velocity: number | null;
};

const getHighestScenarioTitle = (
    scenarios: ComparedScenario[],
    selectMetric: (scenario: ComparedScenario) => number | null
): string => {
    let highestScenarioTitle: string | null = null;
    let highestValue: number | null = null;

    scenarios.forEach(scenario => {
        const value = selectMetric(scenario);
        if (isDefinedNumber(value) && (highestValue === null || value > highestValue)) {
            highestScenarioTitle = `Scenario ${String.fromCharCode(65 + scenario.slotIndex)}`;
            highestValue = value;
        }
    });

    return highestScenarioTitle ?? "-";
};

const ScenarioComparisonSummary: React.FC<ScenarioComparisonSummaryProps> = ({ scenarios }) => {
    const summary = useMemo(() => {
        const populatedScenarios = scenarios.filter((scenario): scenario is ScenarioLibraryScenario & {
            original: ScenarioLibraryScenario["original"] & { payload: CommercialPlanData };
        } => scenario !== null && isCommercialPlanData(scenario.original.payload));

        const comparedScenarios: ComparedScenario[] = populatedScenarios.map(scenario => {
            const metrics = calculateCommercialPlanMetrics(scenario.original.payload);

            return {
                scenario,
                slotIndex: scenarios.indexOf(scenario),
                profit: metrics.profitPercentageChange,
                revenue: metrics.revenuePercentageChange,
                velocity: calculateTotalExpectedVelocity(scenario.original.payload)
            };
        });

        return {
            count: populatedScenarios.length,
            profit: getHighestScenarioTitle(comparedScenarios, scenario => scenario.profit),
            revenue: getHighestScenarioTitle(comparedScenarios, scenario => scenario.revenue),
            velocity: getHighestScenarioTitle(comparedScenarios, scenario => scenario.velocity)
        };
    }, [scenarios]);

    return (
        <div
            className="d-flex flex-wrap align-items-center gap-x-0 gap-y-2 w-100 bg-white border rounded-3 shadow-sm px-3 py-2 mb-3 flex-shrink-0"
            aria-label="Scenario comparison summary"
        >
            <div
                className="pe-4 me-4 border-end text-secondary small text-break"
                style={{ minWidth: 0 }}
            >
                Comparing
                {" "}
                <span className="fw-semibold text-body">
                    {summary.count}
                    {" "}
                    {summary.count === 1 ? "Scenario" : "Scenarios"}
                </span>
            </div>

            <div
                className="pe-4 me-4 border-end text-secondary small text-break"
                style={{ minWidth: 0 }}
            >
                Highest Profit:
                {" "}
                <span className="fw-semibold text-success">
                    {summary.profit}
                </span>
            </div>

            <div
                className="pe-4 me-4 border-end text-secondary small text-break"
                style={{ minWidth: 0 }}
            >
                Highest Revenue:
                {" "}
                <span className="fw-semibold text-primary">
                    {summary.revenue}
                </span>
            </div>

            <div
                className="d-flex align-items-start gap-2 ms-xl-auto text-success small fw-semibold text-break"
                style={{ minWidth: 0 }}
            >
                <SvgIcon icon={trendUpIcon} className="flex-shrink-0 mt-1" />
                <span>
                    Highest Velocity:
                    {" "}
                    {summary.velocity}
                </span>
            </div>
        </div>
    );
};

export default ScenarioComparisonSummary;
